// A video clip's filmstrip on the timeline (issue 349): live repeats one small frame of the clip
// along its block. A file with a poster repeats the poster; one without (no thumbnail worker yet,
// S2) has its first frame drawn once into a small picture, kept for the session by its URL, and
// that picture is repeated instead. Until it is drawn, or where it cannot be (an old browser, a
// file the media host refuses), the block keeps its single still frame.
import { useEffect, useState } from "react";

import { mediaCrossOrigin } from "../../api/media-cookie";
import type { SourceMedia } from "./media";

/** The drawn frame's height in pixels: twice the block's, so it stays sharp on a 2x screen. */
const FRAME_HEIGHT = 96;
/** Where the frame is taken: a moment in, since some browsers paint nothing at 0 (VideoFrame). */
const FRAME_AT_S = 0.1;
/** How long a frame may take to load before the block keeps its still. */
const FRAME_TIMEOUT_MS = 15_000;

const drawn = new Map<string, Promise<string | null>>();
/** The frames still being drawn, each ended as its timeout would end it. */
const pending = new Set<() => void>();

/**
 * Ends every frame still being drawn, as its timeout would: null for it, its video let go. Each test
 * file's end calls it (`tests/setup.ts`), so no wait outlives the page it was made on.
 */
export function endStripFrames(): void {
  // Each `end` takes itself out of the set: a Set's iteration goes on past a deleted entry.
  for (const end of pending) end();
}

/**
 * One frame of the video at `url` as an object URL, or null when it cannot be drawn. A media
 * element reads the signed URL with CORS (the media host allows the app's origin), so the canvas
 * it is drawn on stays readable.
 */
export function stripFrame(url: string): Promise<string | null> {
  const known = drawn.get(url);
  if (known !== undefined) return known;
  const made = drawFrame(url);
  drawn.set(url, made);
  return made;
}

function drawFrame(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    let settled = false;
    const done = (image: string | null) => {
      if (settled) return;
      settled = true;
      pending.delete(end);
      globalThis.clearTimeout(timer);
      video.removeAttribute("src");
      video.load();
      resolve(image);
    };
    const end = () => done(null);
    pending.add(end);
    // The global timer, not the window's: a wait can end after its page is gone (a test's).
    const timer = globalThis.setTimeout(end, FRAME_TIMEOUT_MS);
    // A file's own URL is read with the media cookie, a signed one without (media-cookie.ts).
    video.crossOrigin = mediaCrossOrigin(url);
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.addEventListener("error", () => done(null), { once: true });
    video.addEventListener(
      "seeked",
      () => {
        const { videoWidth, videoHeight } = video;
        const canvas = document.createElement("canvas");
        const pen = videoWidth > 0 && videoHeight > 0 ? canvas.getContext("2d") : null;
        if (pen === null) return done(null);
        canvas.height = FRAME_HEIGHT;
        canvas.width = Math.max(1, Math.round((FRAME_HEIGHT * videoWidth) / videoHeight));
        try {
          pen.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => done(blob === null ? null : URL.createObjectURL(blob)), "image/jpeg", 0.8);
        } catch {
          done(null); // a canvas the browser will not read (no CORS): the block keeps its still
        }
      },
      { once: true },
    );
    video.addEventListener("loadedmetadata", () => {
      video.currentTime = Math.min(FRAME_AT_S, video.duration || FRAME_AT_S);
    }, { once: true });
    video.src = url;
  });
}

/**
 * The picture a clip's block repeats: its file's poster, else its first frame once drawn, else
 * null (the block shows its single still frame meanwhile).
 */
export function useStripImage(source: SourceMedia | undefined): string | null {
  const poster = source?.poster ?? null;
  const url = poster === null && source?.kind === "video" ? source.url : null;
  const [frame, setFrame] = useState<{ url: string; image: string | null } | null>(null);
  useEffect(() => {
    if (url === null) return;
    let current = true;
    void stripFrame(url).then((image) => {
      if (current) setFrame({ url, image });
    });
    return () => {
      current = false;
    };
  }, [url]);
  if (poster !== null) return poster;
  return url !== null && frame?.url === url ? frame.image : null;
}
